import React, { useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useNavigate } from 'react-router-dom'

import { PresentationMetadata } from '@newComponents/Presentation/PresentationMetadata'

import { UserPrimaryPhoto } from '@newComponents/User/UserPrimaryPhoto'
import { FormattedMarkdown } from '@newComponents/FormattedMarkdown/FormattedMarkdown'
import { Icon } from '@newComponents/Icon'
import { Link } from '@newComponents/Link'
import { Text, H2 } from '@newComponents/Text'
import { Button, useCreateLinkPressHandler } from '@newComponents'
import { PresentationVideoPlayer } from '@newComponents/Presentation/PresentationVideoPlayer'

import { XStack, YStack } from 'tamagui'

import PresentationEvents from './PresentationEvents'
import PresentationGuilds from './PresentationGuilds'

import { PresentationScreenContent_presentation$key } from './__generated__/PresentationScreenContent_presentation.graphql'

export const PresentationScreenContent = (props: {
  presentation: PresentationScreenContent_presentation$key
}) => {
  const navigate = useNavigate()
  const presentation = useFragment(
    graphql`
      fragment PresentationScreenContent_presentation on Presentation {
        canEdit
        description
        presenterFirstName
        presenterLastName
        prettyUrl
        title
        videoSourceUrl

        presenter {
          slugId
          firstName
          lastName

          ...UserPrimaryPhotoNew_user
        }
        ...PresentationMetadata_presentation
        ...PresentationVideoPlayerNew_presentation
        ...PresentationEvents_presentation
        ...PresentationGuilds_presentation
      }
    `,
    props.presentation
  )
  const linkAction = useCallback(
    () => navigate(`/presentations/${presentation.prettyUrl}/settings`),
    [presentation.prettyUrl]
  )
  const { linkPressHandler, anchorRef } = useCreateLinkPressHandler(linkAction)

  let presenterElement

  if (presentation.presenter) {
    presenterElement = (
      <XStack gap='$1'>
        <Text size='$4' color='$grey7'>
          Presentation by
        </Text>
        <Link
          size='$4'
          href={`/${presentation.presenter.slugId}`}
          fontWeight='$5'
        >
          {presentation.presenter?.firstName} {presentation.presenter?.lastName}
        </Link>
      </XStack>
    )
  } else {
    presenterElement = (
      <XStack gap='$1'>
        <Text size='$4' color='$grey7'>
          Presentation by
        </Text>

        <Text size='$4' fontWeight='$5'>
          {presentation.presenterFirstName} {presentation.presenterLastName}
        </Text>
      </XStack>
    )
  }

  return (
    <YStack flex={1} flexWrap='wrap' maxWidth='100%' gap='$4'>
      <PresentationMetadata presentation={presentation} />

      <PresentationVideoPlayer presentation={presentation} />

      <YStack gap='$4' flex={1} maxWidth='100%'>
        <XStack>
          <XStack flex={1} gap='$3'>
            {presentation.presenter && (
              <Link href={`/${presentation.presenter.slugId}`}>
                <UserPrimaryPhoto user={presentation.presenter} size='$12' />
              </Link>
            )}

            <YStack flex={1} gap='$2'>
              <H2>{presentation.title}</H2>

              {presenterElement}
            </YStack>
            {!!presentation.canEdit && (
              <Button
                ref={anchorRef as any}
                tag='a'
                href={`/presentations/${presentation.prettyUrl}/settings`}
                secondary
                gap='$1'
                paddingVertical='$2'
                paddingHorizontal='$2'
                minHeight='unset'
                height={32}
                borderRadius={16}
                onPress={linkPressHandler}
                successFlash={false}
                errorFlash={false}
              >
                <Icon name='settings-filled' color='$grey8' size='$4' />

                <Button.Text secondary size='$4'>
                  Settings
                </Button.Text>
              </Button>
            )}
          </XStack>
        </XStack>

        {!!presentation.description && (
          <FormattedMarkdown textSize='$5'>
            {presentation.description}
          </FormattedMarkdown>
        )}
        <YStack gap='$4'>
          <PresentationEvents presentation={presentation} />
          <PresentationGuilds presentation={presentation} />
        </YStack>
      </YStack>
    </YStack>
  )
}

export default PresentationScreenContent
